import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '../Button';
import { __resolve } from '@/lib/i18n';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' | 'fullScreen';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  /** When true, hides the built-in header (title/close button) for custom implementations */
  hideHeader?: boolean;
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
  full: 'max-w-[90vw]',
  fullScreen: 'w-screen h-screen max-w-none',
};

// Global reference counter for managing body scroll lock across nested modals
let modalOpenCount = 0;
let originalBodyOverflow: string | null = null;

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
  hideHeader = false,
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle mount/unmount with animation
  useEffect(() => {
    if (isOpen) {
      // Start rendering and trigger enter animation
      setShouldRender(true);
      setIsClosing(false);
      // Small delay to ensure DOM is ready for animation
      requestAnimationFrame(() => {
        // Animation will trigger via CSS classes
      });
    } else if (shouldRender) {
      // Trigger exit animation
      setIsClosing(true);
      // Wait for exit animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 200); // Match animation duration (--duration-fast: 200ms)
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape || isClosing) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape, isClosing]);

  // Focus management and scroll to top
  useEffect(() => {
    if (isOpen && !isClosing) {
      previousActiveElement.current = document.activeElement as HTMLElement;
      modalRef.current?.focus();
      // Scroll modal container to top when opening
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }
    } else if (!shouldRender) {
      previousActiveElement.current?.focus();
    }
  }, [isOpen, isClosing, shouldRender]);

  // Prevent body scroll when modal is open
  // Use reference counting to handle nested modals correctly
  useEffect(() => {
    if (shouldRender && !isClosing) {
      // Save original overflow style only on first modal
      if (modalOpenCount === 0) {
        originalBodyOverflow = window.getComputedStyle(document.body).overflow;
      }
      
      // Increment counter and lock body scroll
      modalOpenCount++;
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Decrement counter
        modalOpenCount--;
        
        // Only restore scroll when all modals are closed
        if (modalOpenCount === 0 && originalBodyOverflow !== null) {
          document.body.style.overflow = originalBodyOverflow;
          originalBodyOverflow = null;
        }
      };
    }
  }, [shouldRender, isClosing]);

  if (!shouldRender) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-modal overflow-y-auto"
      aria-labelledby={title ? 'modal-title' : undefined}
      aria-describedby={description ? 'modal-description' : undefined}
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 bg-black/50 transition-opacity duration-200',
          isClosing ? 'opacity-0' : 'opacity-100'
        )}
        aria-hidden="true"
        onClick={closeOnOverlayClick && !isClosing ? onClose : undefined}
      />

      {/* Modal */}
      <div className={cn(
        "flex min-h-full items-start justify-center",
        size === 'fullScreen' ? 'p-0' : 'p-4 sm:p-6 md:p-8'
      )}>
        <div
          ref={modalRef}
          tabIndex={-1}
          className={cn(
            'relative w-full bg-background shadow-xl',
            // DIALOG-LAYOUT: flex column so header/footer stay pinned while body
            // scrolls internally. max-h bounds the modal to the viewport so the
            // footer is never pushed below the fold regardless of content length.
            'flex flex-col',
            'transform transition-all duration-200 ease-out',
            isClosing
              ? 'opacity-0 scale-95 animate-scale-out'
              : 'opacity-100 scale-100 animate-scale-in',
            size === 'fullScreen'
              ? 'rounded-none h-screen'
              : 'rounded-lg my-8 max-h-[calc(100vh-4rem)]',
            sizeClasses[size],
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - flex-shrink-0 so it stays pinned at the top */}
          {!hideHeader && size !== 'fullScreen' && (
            <div className="flex-shrink-0 flex items-start justify-between p-6 pb-4">
              <div>
                {title && (
                  <h2 id="modal-title" className="text-lg font-semibold text-foreground">
                    {title}
                  </h2>
                )}
                {description && (
                  <p id="modal-description" className="mt-1 text-sm text-muted-foreground">
                    {description}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="ml-4 rounded-md p-1 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Close modal"
              >
                <svg className="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Body - flex-1 + overflow-y-auto so long content scrolls INSIDE
              the modal rather than pushing the footer below the viewport.
              min-h-0 is required so flex-1 can shrink below content height. */}
          <div className={cn(
            'flex-1 overflow-y-auto min-h-0',
            size === 'fullScreen' ? '' : 'px-6 py-4'
          )}>
            {children}
          </div>

          {/* Footer - flex-shrink-0 so it stays pinned at the bottom */}
          {footer && size !== 'fullScreen' && (
            <div className="flex-shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Dialog component - a specific type of modal with standard actions
export interface DialogProps extends Omit<ModalProps, 'footer'> {
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'destructive' | 'danger';
  isLoading?: boolean;
  /** data-testid applied to the confirm button. Used for targeted Playwright selectors. */
  confirmTestId?: string;
  /** data-testid applied to the cancel button. Used for targeted Playwright selectors. */
  cancelTestId?: string;
}

export function Dialog({
  onConfirm,
  confirmText = __resolve('ui::Confirm'),
  cancelText = __resolve('ui::Cancel'),
  confirmVariant = 'primary',
  isLoading = false,
  confirmTestId,
  cancelTestId,
  ...modalProps
}: DialogProps) {
  return (
    <Modal
      {...modalProps}
      footer={
        <>
          <Button variant="ghost" onClick={modalProps.onClose} disabled={isLoading} data-testid={cancelTestId}>
            {cancelText}
          </Button>
          {onConfirm && (
            <Button variant={confirmVariant} onClick={onConfirm} isLoading={isLoading} data-testid={confirmTestId}>
              {confirmText}
            </Button>
          )}
        </>
      }
    />
  );
}
